import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { Tasks } from 'models/TasksModel';
import AsyncSelect from 'react-select/async';

const TasksForm: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.TASKS, Code.Form.Name.TASKS];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [priorityOptions, setPriorityOptions] = Hooks.Form.Options();
  const [typeOptions, setTypeOptions] = Hooks.Form.Options();
  const [statusOptions, setStatusOptions] = Hooks.Form.Options();
  const [receiverOptions, setReceiverOptions] = Hooks.Form.Options();
  const [allUserList, setAllUserList] = Hooks.Form.Options();
  const [selectedUser, setSelectedUser] = useState<string[]>([]);
  const [formData, setFormData] = useState<Tasks>({});
   
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.OPTION+Code.Group.TASKS_TYPE, Code.Option.Common.SELECT, setTypeOptions, setError);
    Files.Json.API(Code.File.OPTION+Code.Group.TASKS_STATUS, Code.Option.Common.NA, setStatusOptions, setError);
    Files.Json.API(Code.File.USER, Code.Option.Common.NA, setReceiverOptions, setError);
    Files.Json.API(Code.File.USER, Code.Option.Common.NOTHING, setAllUserList, setError);
    Files.Json.Http(Code.File.PRIORITY, Code.Option.Common.NA, setPriorityOptions, setError);

    API.Data.GetByKeyId<Tasks>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.tasks_active); 
        const clientData = await API.Data.getClientNameNumber(data.client_id);
        const formData = await Form.Map.Data(data);
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
          ...clientData, 
        }));
        setSelectedUser(JSON.parse(data.tasks_receiver_list!));
      }})
      setLoading(false);
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    const addonData = await API.Data.AddonData(formData, formName, keyId);
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      ...addonData,
      sender_id: Utility.Storage.UserId(),
      tasks_receiver_list: JSON.stringify(selectedUser),
    });

    //console.log("finalData:",finalData.receiver_id);

    API.Data.Save(formName, keyId, finalData, setMessage)
  };

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} clientNumber={formData.client_number} formName={formName} Privilege={Privilege} />
          <Control.Tab.Header titles={["Details","More"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
            
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Select id={Name.Common.TypeId} value={formData.type_id} title={Title.Type} options={typeOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,32,1]} require={true} />
                <Field.Select id={Name.Common.StatusId} value={formData.status_id} title={Title.Status} options={statusOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,36,1]} require={true}  />
                <Field.Date id={Name.Tasks.Date} value={formData.tasks_date} title={Title.Date} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,32,0]}  require={true} currentDate={true} />
              </Control.Row>
              <Control.Box>
                <Field.FilterSelect id={Name.Common.ReceiverId} value={formData.receiver_id} title="Assigned to" options={receiverOptions} setFormData={setFormData} require={true} />
              </Control.Box>
              <Control.Row>
                <Field.Header id={Name.Client.Id} title={Title.Client} require={true}>
                  <AsyncSelect id={Name.Client.Id} loadOptions={(inputValue) => API.Data.AsyncOption(inputValue, Code.Form.Name.CLIENT, Code.Mapping.client.value, Code.Mapping.client.label)} styles={Code.Style.FormOption} onChange={(selected) => Utility.Handle.ClientChange(selected, setFormData)} value={Utility.Value.Async(formData.client_id,formData.client_name)} placeholder="Search" />
                </Field.Header>
              </Control.Row>
              <Control.Box>
                <Field.Text id={Name.Tasks.Name} value={formData.tasks_name} title="Subject" onChange={(e) => Form.Field.Change(e, formData, setFormData)}  require={true} />
                <Field.Area id={Name.Tasks.Comment} value={formData.tasks_comment} title={Title.Comment} onChange={(e) => Form.Field.Change(e, formData, setFormData)} height={300} />
              </Control.Box>
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <Control.Row>
                <Field.Select id={Name.Common.PriorityId} value={formData.priority_id} title={Title.Type} options={priorityOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
                <Field.Date id={Name.Tasks.EndDate} value={formData.tasks_end_date} title="Complete on" onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]}  />
              </Control.Row>
              <Control.Box>
                <Field.MultiSelect title="Also assigned to" id={Title.User} options={allUserList} selectedItems={selectedUser} setSelectedItems={setSelectedUser}/>
              </Control.Box>
            </Control.Tab.Content>
            
          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default TasksForm;